import { CheckoutForm } from './checkout-form';
import { useIntl } from 'shared/intl/use-intl';
import { useState, useCallback, useRef } from 'preact/hooks';
import * as pmtmath from 'shared/payments/math';
import * as pmtfmt from 'shared/payments/fmt';
import * as stripeutil from 'shared/payments/stripe';
import { CouponRow, PriceRow } from 'server/types';
import { StripeContext, StripeInput } from './stripe';
import { useCurrentUser } from 'client/lib/auth';
import { FormGroup } from '@components/async-form';
import { showError } from '@components/app-error';
import { CheckoutFn, PurchaseFn, StripeHandlerFn } from 'shared/payments/types';

import { UpsellStripeCheckoutForm, isUpsellable } from './upsell-stripe-checkout-form';
import { TrustStrip } from './trust-strip';
import { TaxSummary } from './tax-summary';
import { rpx } from 'client/lib/rpx-client';

import {
  processStripePayment,
  describeCheckoutError,
  loggableCheckoutError,
} from './checkout-logic';
import { reportCheckoutError } from './report-checkout-error';
export { buildPaymentReturnUrl, processStripePayment } from './checkout-logic';

export function PayWhatYouCanInput({
  price,
  value,
  onChange,
  variant = 'slider',
  explicitCurrency,
}: {
  price: PriceRow;
  value: number;
  onChange(cents: number): void;
  variant?: 'slider' | 'box';
  /** The page offers more than one currency, so a bare $ would be ambiguous. */
  explicitCurrency?: boolean;
}) {
  const [showCustomInput, setShowCustomInput] = useState(false);
  const symbol = explicitCurrency
    ? pmtfmt.explicitCurrencySymbol(price.currency)
    : pmtfmt.currencySymbol(price.currency);
  const zeroDecimal = pmtfmt.isZeroDecimal(price.currency);
  const effectiveMin = Math.max(price.minPriceInCents, stripeutil.minPrice(price.currency));
  const anchorPrice = price.priceInCents;
  const sliderMax = Math.max(anchorPrice * 5, effectiveMin + 10000, 50000);
  const sliderRange = sliderMax - effectiveMin;
  const anchorPercent = ((anchorPrice - effectiveMin) / sliderRange) * 100;
  const snapThreshold = anchorPrice * 0.03;
  const sliderStep = zeroDecimal
    ? Math.max(1, Math.floor(sliderRange / 200))
    : Math.max(100, Math.floor(sliderRange / 200));
  const showAnchor = anchorPercent > 25 && anchorPercent < 75;
  const showAnchorMark = showAnchor && Math.abs(value - anchorPrice) > snapThreshold;
  const clampedSliderValue = Math.min(value, sliderMax);
  const displayValue = zeroDecimal ? value.toString() : Math.round(value / 100).toString();
  const minDisplay = zeroDecimal ? effectiveMin : Math.round(effectiveMin / 100);
  const formattedValue = `${symbol}${displayValue}`;
  const [editText, setEditText] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  function fmtPrice(cents: number) {
    const dollars = zeroDecimal ? cents : Math.round(cents / 100);
    return `${symbol}${dollars}`;
  }

  function handleSliderInput(e: Event) {
    const raw = parseInt((e.target as HTMLInputElement).value);
    if (Math.abs(raw - anchorPrice) <= snapThreshold) {
      onChange(anchorPrice);
    } else {
      onChange(raw);
    }
  }

  function commitTextValue(text: string) {
    const raw = parseFloat(text || '0');
    if (isNaN(raw) || raw <= 0) {
      onChange(effectiveMin);
    } else {
      const dollars = Math.round(raw);
      const cents = Math.max(effectiveMin, zeroDecimal ? dollars : dollars * 100);
      onChange(cents);
    }
    setIsEditing(false);
  }

  function handleTextFocus() {
    setEditText(displayValue);
    setIsEditing(true);
  }

  function handleTextBlur() {
    commitTextValue(editText);
  }

  function handleTextKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      commitTextValue(editText);
      (e.target as HTMLInputElement).blur();
    }
  }

  function handleTextInput(e: Event) {
    setEditText((e.target as HTMLInputElement).value);
  }

  if (variant === 'box') {
    return (
      <div style="border: 2px solid #0b21c2; border-radius: 14px; background: #f5f8ff; padding: 20px; margin-bottom: 16px">
        <div style="margin-bottom: 16px">
          <span style="display: inline-block; background: #0b21c2; color: #fff; font-size: 16px; font-weight: 800; padding: 8px 20px; border-radius: 999px">
            Pay what you want (minimum {symbol}
            {minDisplay})
          </span>
        </div>
        <div style="display: flex; align-items: center; gap: 8px">
          <span style="font-size: 26px; font-weight: 800; color: #0b21c2">{symbol}</span>
          <input
            type="text"
            inputMode="numeric"
            value={isEditing ? editText : displayValue}
            onFocus={handleTextFocus}
            onBlur={handleTextBlur}
            onKeyDown={handleTextKeyDown}
            onInput={handleTextInput}
            aria-label="Pay what you want amount"
            style="width: 280px; border: 2px solid #0b21c2; border-radius: 12px; padding: 12px 16px; font-size: 28px; font-weight: 800; color: #0b21c2; outline: none"
          />
        </div>
      </div>
    );
  }

  return (
    <div class="mb-4">
      <div class="flex items-baseline justify-between mb-3">
        <label class="text-sm font-medium text-gray-700" for="pwyw-slider">
          Choose your amount
        </label>
        <span class="text-2xl font-bold text-gray-900">{formattedValue}</span>
      </div>

      <div class="pwyw-slider-container">
        {showAnchorMark && (
          <div class="pwyw-anchor-mark" style={{ left: `${anchorPercent}%` }} aria-hidden="true" />
        )}
        <input
          type="range"
          id="pwyw-slider"
          min={effectiveMin}
          max={sliderMax}
          step={sliderStep}
          value={clampedSliderValue}
          onInput={handleSliderInput}
          aria-valuetext={formattedValue}
          class="w-full"
        />
      </div>

      <div
        class="relative text-xs text-gray-500 mb-3"
        style="display: flex; justify-content: space-between"
      >
        <span>{fmtPrice(effectiveMin)}</span>
        {showAnchor && (
          <span
            class="font-medium text-blue-700"
            style={`position: absolute; left: ${anchorPercent}%; transform: translateX(-50%)`}
          >
            Suggested: {fmtPrice(anchorPrice)}
          </span>
        )}
        <span>{fmtPrice(sliderMax)}</span>
      </div>

      {!showCustomInput && (
        <button
          type="button"
          class="text-xs text-blue-600 hover:text-blue-800 underline"
          onClick={() => setShowCustomInput(true)}
        >
          Enter a custom amount
        </button>
      )}

      {showCustomInput && (
        <div class="flex items-center gap-2">
          <span class="text-sm text-gray-500">{symbol}</span>
          <input
            type="text"
            inputMode="numeric"
            value={isEditing ? editText : displayValue}
            onFocus={handleTextFocus}
            onBlur={handleTextBlur}
            onKeyDown={handleTextKeyDown}
            onInput={handleTextInput}
            aria-label="Custom amount"
            class="ruz-input text-sm w-28"
          />
        </div>
      )}
    </div>
  );
}

export function StripeCheckoutForm(props: {
  collectAddress: boolean;
  price: PriceRow;
  coupon?: CouponRow;
  isGift?: boolean;
  actionText?: string;
  /**
   * If handleStripe is passed in, the caller is responsible for invoking Stripe
   * checkout themselves-- this is used by the upsell modal to invert control
   * so that it can perform N checkouts in a sequence.
   */
  handleStripe?: StripeHandlerFn;
  beginPurchase: PurchaseFn;
  onPurchaseComplete(): Promise<unknown>;
}) {
  const { collectAddress, price, coupon, isGift, beginPurchase, onPurchaseComplete } = props;
  const intl = useIntl();
  const [ctx, setCtx] = useState<StripeContext | undefined>(undefined);
  const user = useCurrentUser()!;
  const freeTrialPeriod = pmtmath.freeTrialPeriod({ price, coupon });
  const paymentRequired = !freeTrialPeriod && !!pmtmath.initialPrice({ price, coupon });
  const [isLoading, setIsLoading] = useState(false);
  const [cardError, setCardError] = useState<string | undefined>(undefined);
  const [customAmount, setCustomAmount] = useState(price.priceInCents);
  const [taxResult, setTaxResult] = useState<
    | {
        subtotal: number;
        taxAmount: number;
        total: number;
        breakdown: Array<{
          amount: number;
          rate: string;
          percentage: number;
          jurisdiction: string;
        }>;
      }
    | undefined
  >(undefined);
  const [taxLoading, setTaxLoading] = useState(false);
  const taxDebounceRef = useRef<ReturnType<typeof setTimeout>>();
  const lastAddressRef = useRef<{ country?: string; postal_code?: string }>({});

  const fetchTax = useCallback(
    (address: { country?: string; postal_code?: string; state?: string }, amount?: number) => {
      if (!address.country || !address.postal_code) {
        return;
      }
      if (taxDebounceRef.current) {
        clearTimeout(taxDebounceRef.current);
      }
      lastAddressRef.current = address;
      taxDebounceRef.current = setTimeout(async () => {
        setTaxLoading(true);
        try {
          const result = await rpx.courseCheckout.calculateCheckoutTax({
            priceId: price.id,
            couponId: coupon?.id,
            customAmountInCents: price.payWhatYouCan ? (amount ?? customAmount) : undefined,
            address: {
              country: address.country!,
              postal_code: address.postal_code!,
              state: address.state,
            },
          });
          setTaxResult(result);
        } catch {
          setTaxResult(undefined);
        } finally {
          setTaxLoading(false);
        }
      }, 400);
    },
    [price.id, coupon?.id, price.payWhatYouCan, customAmount],
  );

  if (isUpsellable(price)) {
    return <UpsellStripeCheckoutForm {...props} />;
  }

  let actionText = props.actionText;
  if (!actionText) {
    actionText =
      !paymentRequired && freeTrialPeriod
        ? intl('Start Free Trial →')
        : isGift
          ? intl('Purchase Gift →')
          : price.payWhatYouCan
            ? intl('Pay Now →')
            : intl('Buy Now →');
  }

  const isPwyc = price.payWhatYouCan;
  const pwycEffectiveMin = Math.max(price.minPriceInCents, stripeutil.minPrice(price.currency));
  const pwycAmountValid = !isPwyc || customAmount >= pwycEffectiveMin;

  return (
    <CheckoutForm
      actionText={actionText}
      belowButton={<TrustStrip />}
      onSubmit={async () => {
        if (!ctx || !user) {
          return;
        }
        if (isPwyc && !pwycAmountValid) {
          return;
        }

        try {
          setIsLoading(true);
          setCardError(undefined);
          const checkout: CheckoutFn = async ({ priceId, couponId }) => {
            const result = await processStripePayment({
              ctx,
              user,
              priceId,
              couponId,
              processPayment: beginPurchase,
              customAmountInCents: isPwyc ? customAmount : undefined,
            });
            return !!result && result.type !== 'validationError';
          };
          const success = props.handleStripe
            ? await props.handleStripe({ checkout })
            : await checkout({ priceId: price.id, couponId: coupon?.id });
          if (!success) {
            return;
          }
          await onPurchaseComplete();
          setCardError(undefined);
        } catch (err) {
          const described = describeCheckoutError(err);
          reportCheckoutError({ step: 'checkout', priceId: price.id, err });
          if (described.kind === 'card') {
            setCardError(described.message);
            console.warn('checkout card error', loggableCheckoutError(err));
          } else {
            showError(err);
          }
        } finally {
          setIsLoading(false);
        }
      }}
    >
      {isPwyc && (
        <PayWhatYouCanInput
          price={price}
          value={customAmount}
          onChange={(cents) => {
            setCustomAmount(cents);
            if (lastAddressRef.current?.country) {
              fetchTax(lastAddressRef.current, cents);
            }
          }}
        />
      )}
      <FormGroup prop="paymentMethod">
        <StripeInput
          customerName={user?.name}
          priceId={price.id}
          onReady={setCtx}
          collectAddress={collectAddress || false}
          disabled={isLoading}
          onAddressChange={paymentRequired ? fetchTax : undefined}
          amount={isPwyc ? customAmount : pmtmath.initialPrice({ price, coupon })}
          currency={price.currency}
          mode={
            freeTrialPeriod
              ? 'setup'
              : price.paymentType === 'subscription' || price.paymentType === 'paymentplan'
                ? 'subscription'
                : 'payment'
          }
        />
      </FormGroup>
      {cardError && (
        <div
          role="alert"
          class="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {cardError}
        </div>
      )}
      {collectAddress && paymentRequired && (taxLoading || taxResult) && (
        <TaxSummary
          subtotal={taxResult?.subtotal ?? 0}
          taxAmount={taxResult?.taxAmount ?? 0}
          total={taxResult?.total ?? 0}
          breakdown={taxResult?.breakdown ?? []}
          currency={price.currency}
          loading={taxLoading}
        />
      )}
      {!paymentRequired && (
        <p class="text-gray-600 text-center mt-4">
          {!!freeTrialPeriod &&
            intl(`You will not be charged until the end of the free trial period.`)}
          {!freeTrialPeriod && intl(`You will not be charged today.`)}
        </p>
      )}
    </CheckoutForm>
  );
}
